export const rustAuthExample = `use chrono::Utc;
use hmac::{Hmac, Mac};
use reqwest::{header, Client};
use serde::{Deserialize, Serialize};
use serde_json::{json, Map, Value};
use sha2::Sha256;
use std::env;
use std::error::Error;
use uuid::Uuid;

type HmacSha256 = Hmac<Sha256>;

// API Configuration
const API_BASE_URL: &str = "https://app.reasonshield.com/api/v1";

#[derive(Debug, Serialize, Deserialize)]
struct ApiResponse {
    data_id: Option<String>,
    status: Option<String>,
    message: Option<String>,
    #[serde(flatten)]
    other: Map<String, Value>,
}

#[tokio::main]
async fn main() -> Result<(), Box<dyn Error>> {
    // Get credentials from environment
    let api_key = env::var("VENDOR_API_KEY").expect("VENDOR_API_KEY not set");
    let webhook_secret = env::var("VENDOR_WEBHOOK_SECRET").expect("VENDOR_WEBHOOK_SECRET not set");
    let vendor_id = env::var("VENDOR_ID").expect("VENDOR_ID not set");
    
    // Create test data payload
    let test_data = json!({
        "test_id": "12345",
        "panel_type": "RESPIRATORY"
    });
    
    // Create full payload
    let mut payload = Map::new();
    payload.insert("vendor_id".to_string(), json!(vendor_id));
    payload.insert("timestamp".to_string(), json!(Utc::now().to_rfc3339()));
    payload.insert("request_id".to_string(), json!(Uuid::new_v4().to_string()));
    payload.insert("test_data".to_string(), test_data);
    
    let payload_value = Value::Object(payload.clone());
    
    // Make the authenticated request
    match make_authenticated_request(
        "/lis-webhook/vendor/submit-data",
        "POST",
        Some(&payload_value),
        &api_key,
        &webhook_secret,
    ).await {
        Ok(response) => {
            println!("Data submitted successfully. ID: {}", 
                response.data_id.unwrap_or_else(|| "unknown".to_string()));
        }
        Err(err) => {
            eprintln!("Error: {}", err);
        }
    }
    
    Ok(())
}

/// Makes an authenticated request to the ReasonShield API using dual authentication
async fn make_authenticated_request(
    endpoint: &str,
    method: &str,
    payload: Option<&Value>,
    api_key: &str,
    webhook_secret: &str,
) -> Result<ApiResponse, Box<dyn Error>> {
    let url = format!("{}{}", API_BASE_URL, endpoint);
    let client = Client::new();
    
    // Create request builder
    let mut request_builder = match method {
        "GET" => client.get(&url),
        "POST" => client.post(&url),
        "PUT" => client.put(&url),
        "DELETE" => client.delete(&url),
        _ => return Err(format!("Unsupported HTTP method: {}", method).into()),
    };
    
    // Add API key authentication
    request_builder = request_builder.header(
        header::AUTHORIZATION, 
        format!("Bearer {}", api_key)
    );
    
    // For methods with a payload
    if let Some(payload_value) = payload {
        // Convert to canonical JSON string (sorted keys)
        let payload_string = serde_json::to_string(payload_value)?;
        
        // Generate signature
        let signature = generate_signature(&payload_string, webhook_secret)?;
        
        // Add signature header and JSON body
        request_builder = request_builder
            .header("X-Vendor-Signature", signature)
            .header(header::CONTENT_TYPE, "application/json")
            .body(payload_string);
    }
    
    // Send the request
    let response = request_builder.send().await?;
    
    // Check status
    if !response.status().is_success() {
        let status = response.status();
        let text = response.text().await?;
        return Err(format!("API request failed with status {}: {}", status, text).into());
    }
    
    // Parse the response
    let api_response: ApiResponse = response.json().await?;
    Ok(api_response)
}

/// Generates an HMAC-SHA256 signature for the payload
fn generate_signature(payload: &str, secret: &str) -> Result<String, Box<dyn Error>> {
    let mut mac = HmacSha256::new_from_slice(secret.as_bytes())?;
    mac.update(payload.as_bytes());
    let result = mac.finalize();
    let signature = hex::encode(result.into_bytes());
    Ok(signature)
}`; 